import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';


const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  footer,
  icon,
  iconColor
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      
      // Focus the modal when it opens
      setTimeout(() => {
        modalRef?.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
      
      // Return focus to the element that opened the modal
      if (previousActiveElement?.current) {
        previousActiveElement?.current?.focus();
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef?.current) return;

    const handleTabKey = (e) => {
      if (e?.key !== 'Tab') return;

      const focusableElements = modalRef?.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
      );
      const firstElement = focusableElements?.[0];
      const lastElement = focusableElements?.[focusableElements?.length - 1];

      if (e?.shiftKey && document.activeElement === firstElement) {
        e?.preventDefault();
        lastElement?.focus();
      } else if (!e?.shiftKey && document.activeElement === lastElement) {
        e?.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[95vw]'
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.2,
        ease: [0.16, 1, 0.3, 1] // Custom easing for smooth feel
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: { 
        duration: 0.15,
        ease: 'easeIn'
      }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "modal-title" : undefined}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeOnOverlayClick ? onClose : undefined}
            aria-hidden="true"
          />

          {/* Modal Content */}
          <motion.div
            ref={modalRef}
            className={`relative bg-black border border-white/12 rounded-md shadow-2xl w-full ${sizeClasses?.[size]} max-h-[90vh] flex flex-col`}
            variants={modalVariants}
            onClick={(e) => e?.stopPropagation()}
            tabIndex={-1}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="p-4 md:p-6 border-b border-white/12 flex items-center justify-between bg-white/5">
                <div className="flex items-center gap-3">
                  {icon && (
                    <Icon 
                      name={icon} 
                      size={24} 
                      color={iconColor || '#FFFFFF'}
                      aria-hidden="true"
                    />
                  )}
                  {title && (
                    <h2 id="modal-title" className="text-lg md:text-xl font-heading font-semibold text-white">
                      {title}
                    </h2>
                  )}
                </div>
                {showCloseButton && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    iconName="X"
                    iconSize={24}
                    iconColor="#FFFFFF"
                    onClick={onClose}
                    className="hover:bg-white/10 relative z-10 pointer-events-auto"
                    style={{ color: '#FFFFFF' }}
                    aria-label="إغلاق النافذة"
                  />
                )}
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="p-4 md:p-6 border-t border-white/12 bg-white/5">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;